import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import axiosInstance from "@/lib/axios";
import { automationEndpoint, leadsEndpoints } from "@/config/endpoints";
import { invalidateLeadCaches, invalidateLeadTimeline } from "@/lib/queryClient";

export interface LeadAutomationFailure {
  id: number;
  automationId: number;
  automationName: string;
  /** Raw event enum, e.g. `SUBSTATUS_CHANGED`. Kept for completeness. */
  triggerEvent: string | null;
  /** Sentence-case version of `triggerEvent`, e.g. "Sub-status changed". */
  triggerLabel: string | null;
  entityType: "lead" | "child" | null;
  entityId: number | null;
  /** Display name of the entity — deal name for children, lead name for leads. */
  entityName: string | null;
  status: "FAILED";
  error: string | null;
  startedAt: string;
  completedAt: string | null;
}

interface FailuresResponse {
  status: string;
  failures: LeadAutomationFailure[];
}

export const LEAD_AUTOMATION_FAILURES_KEY = "lead-automation-failures";

/**
 * Fetches recent FAILED automation runs scoped to a specific entity
 * (the lead row OR a single child/deal row) — NOT lead-wide. Automations
 * fire per-child for sub-status changes, so a deal-view shows only that
 * deal's failures and a lead-view shows only lead-level failures.
 *
 * The server also dedupes by automationId — only the LATEST failure per
 * automation is returned, so a sub-status that bounced 5 times only
 * surfaces one banner row, not five.
 *
 * @param leadId   the URL-path lead id (the route is still mounted under
 *                 /leads/:id even when we're viewing a child)
 * @param scope    `{entityType, entityId}` — what's actually in view
 */
export function useLeadAutomationFailures(
  leadId: number | string | null | undefined,
  scope: {
    entityType: "lead" | "child";
    entityId: number | string | null | undefined;
  },
  options: { enabled?: boolean; limit?: number } = {},
) {
  const enabled =
    (options.enabled ?? true) &&
    leadId != null &&
    leadId !== "" &&
    scope.entityId != null &&
    scope.entityId !== "";
  return useQuery({
    queryKey: [
      LEAD_AUTOMATION_FAILURES_KEY,
      leadId,
      scope.entityType,
      scope.entityId,
      options.limit ?? 10,
    ],
    enabled,
    queryFn: async () => {
      const { data } = await axiosInstance.get<FailuresResponse>(
        `${leadsEndpoints}/${leadId}/automation-failures`,
        {
          params: {
            entityType: scope.entityType,
            entityId: scope.entityId,
            limit: options.limit ?? 10,
          },
        },
      );
      return data.failures;
    },
  });
}

interface RetryResponse {
  status: string;
  retried: boolean;
  originalRunId: number;
  automationId: number;
  newRunId: number | null;
  /**
   * Outcome of the new AutomationRun the retry produced. The retry is
   * synchronous in the engine, so by the time the HTTP response comes
   * back the new run is already finalized.
   *   SUCCESS  — automation ran and all actions completed
   *   FAILED   — at least one action errored; banner row stays
   *   SKIPPED  — frequency guard or condition mismatch (treat as a
   *              non-error informational outcome)
   */
  newStatus: "SUCCESS" | "FAILED" | "SKIPPED" | "RUNNING" | "PENDING" | null;
  /** Error message when newStatus === "FAILED". */
  error: string | null;
}

export function useRetryAutomationRun(
  leadId: number | string | null | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (runId: number) => {
      const { data } = await axiosInstance.post<RetryResponse>(
        `${automationEndpoint}/runs/${runId}/retry`,
      );
      return data;
    },
    onSuccess: (data) => {
      // The retry is synchronous server-side — `data.newStatus` reflects
      // the outcome of the fresh run, not just that the HTTP call landed.
      // Toast accordingly so users know whether the underlying problem
      // is actually resolved.
      if (data.newStatus === "SUCCESS") {
        toast.success("Automation re-ran successfully");
      } else if (data.newStatus === "SKIPPED") {
        toast.info("Automation skipped — already up to date");
      } else if (data.newStatus === "FAILED") {
        toast.error(
          data.error
            ? `Retry failed: ${data.error}`
            : "Retry failed — see automation logs for details",
        );
      } else {
        toast.success("Automation retried");
      }
      // The retry creates a NEW AutomationRun; the original FAILED one
      // stays in history. The list query orders "latest run per
      // automation" and filters to currently-failed, so on a SUCCESS
      // retry the row drops off the banner naturally.
      if (leadId != null) {
        queryClient.invalidateQueries({
          queryKey: [LEAD_AUTOMATION_FAILURES_KEY, leadId],
        });
      }
      // A retried automation almost always mutates lead/deal state —
      // change stage, move sub-status, assign a user, add a follow-up,
      // etc. Bust the full lead-cache prefix so the surrounding panel
      // (stage dropdown, follow-ups, assigned list, activity log) shows
      // the resulting state without the user manually refreshing.
      invalidateLeadCaches();
      // invalidateLeadCaches no longer busts the timeline (2026-09-12) —
      // refresh the drawer's activity log for this lead explicitly.
      invalidateLeadTimeline(leadId);
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to retry automation";
      toast.error(msg);
    },
  });
}
